import { isAfter } from 'date-fns';
import type { Program, ProgramTimelineContentObject, Topic } from 'models';
import { can } from 'old/utils/permissions';
import { Fragment, useEffect, useState } from 'react';
import { Flex, Text, Title } from 'ui';
import { ProgramTopic } from '../../program-topic';

type FilterState = {
  sort: string;
  type: string[];
};

const defaultFilter: FilterState = {
  sort: 'date_created',
  type: ['post', 'event', 'poll', 'activity', 'withings_order_post'],
};

const getSortName = (item: ProgramTimelineContentObject) => {
  switch (item.type) {
    case 'activity':
      return item.activity.name;
    case 'event':
      return item.event.name;
    case 'poll':
      return item.poll.title;
    case 'post':
      return item.post.title ?? item.post.textContent;
    case 'withings_order_post':
      return item.withings_order_post.title ?? item.withings_order_post.textContent;
  }
};

const sortPosts = (items: ProgramTimelineContentObject[], sort: string) => {
  switch (sort) {
    case 'name':
      return items.toSorted((a, b) => (getSortName(a) ?? '').localeCompare(getSortName(b) ?? ''));
    case 'type':
      return items.toSorted((a, b) => a.type.localeCompare(b.type));
    default:
      return items;
  }
};

type P = {
  items: ProgramTimelineContentObject[];
  loading: boolean;
  onSelectPost: (post?: { id: string }) => void;
  onSelectTopic: (topic?: { id: string }) => void;
  program: Program;
  topics: Topic[];
};

/**
 * Bespoke port of the legacy timeline component, rebuilt on Flex/Typography/ui-icons internally.
 * Kept as-is behaviorally/visually, not redesigned.
 * @deprecated AI-generated code, not reviewed. Needs revision before further use — do not use as a reference or pattern to follow.
 */
const ProgramTopicsList = ({ items, loading, onSelectPost, onSelectTopic, program, topics }: P) => {
  const [selected, setSelected] = useState(0);
  const [selectedPost, setSelectedPost] = useState(0);
  const [filterState, setFilterState] = useState<FilterState>(defaultFilter);

  useEffect(() => {
    if (topics.length > 0) {
      onSelectTopic(topics[selected]);
    }

    setFilterState(defaultFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Mirrors the legacy componentDidMount, runs once on mount only
  }, []);

  const handleSelectTopic = (topicIndex: number) => {
    setSelectedPost(0);
    setSelected(topicIndex);
    onSelectTopic(topics[topicIndex]);
    setFilterState(defaultFilter);
  };

  const handleSelectPost = (_topic: unknown, post: ProgramTimelineContentObject, postIndex: number) => {
    setSelectedPost(postIndex);

    onSelectPost(post);
    document.querySelector('#programNavigation')?.scrollIntoView();
  };

  const filterPosts = (sort: Partial<FilterState>) => {
    setFilterState((current) => ({ ...current, ...sort }));
  };

  const setFilter = (type: string[]) => {
    setFilterState((current) => ({ ...current, type }));
  };

  const currentTime = new Date();
  const filteredPosts = sortPosts(items, filterState.sort).filter((post) => filterState.type.includes(post.type));
  const posts = can(program, 'Program', 'seeFuturePosts')
    ? filteredPosts
    : filteredPosts.filter((post) => isAfter(currentTime, post.publishAt));

  return (
    <>
      <Title className="program__header" level={4}>
        Modules
      </Title>
      {topics.map((topic, index) => (
        <Fragment key={topic.id}>
          <ProgramTopic
            filter={filterState}
            filterPosts={filterPosts}
            index={index}
            onSelectPost={handleSelectPost}
            onSelectTopic={handleSelectTopic}
            posts={posts}
            program={program}
            selected={selected === index}
            selectedIndex={selectedPost}
            setFilter={setFilter}
            topic={topic}
          />
          {selected === index && posts.length === 0 && !loading ? (
            <Flex justify="center">
              <Text size="large" type="primary">
                No content yet
              </Text>
            </Flex>
          ) : null}

          {index < topics.length - 1 ? <div className="list-separator" /> : null}
        </Fragment>
      ))}
    </>
  );
};

export default ProgramTopicsList;
